import React from 'react';
import { Box, Typography, Select, MenuItem, FormControl, IconButton } from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

// ── Styled components ────────────────────────────────────────────────────────

const PaginationWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2.5),
  width: '100%',
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
  },
}));

const PaginationPill = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  borderRadius: '30px',
  padding: theme.spacing(0.5, 1),
  gap: theme.spacing(0.5),
  minHeight: '44px',
  maxWidth: 'fit-content',
  margin: '0 auto',
}));

const PageNumber = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  cursor: active ? 'default' : 'pointer',
  fontSize: '0.875rem',
  fontWeight: active ? 700 : 500,
  transition: 'all 0.2s ease',
  background: active
    ? 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)'
    : 'transparent',
  color: active
    ? '#ffffff'
    : theme.palette.text.secondary,
  boxShadow: active ? '0 2px 8px rgba(168,70,155,0.35)' : 'none',
  '&:hover': {
    background: active
      ? 'linear-gradient(135deg, #9a3d8d 0%, #4589be 100%)'
      : alpha(theme.palette.primary.main, 0.1),
    color: active ? '#ffffff' : theme.palette.text.primary,
  },
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  width: '34px',
  height: '34px',
  color: theme.palette.text.secondary,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    color: alpha(theme.palette.text.disabled, 0.4),
  },
}));

const RowsSelect = styled(FormControl)(({ theme }) => ({
  minWidth: '155px',
  '& .MuiOutlinedInput-root': {
    height: '36px',
    borderRadius: theme.shape.borderRadius * 1.5,
    fontSize: '0.875rem',
  },
  '& .MuiSelect-select': {
    padding: theme.spacing(0.75, 1.25),
    fontSize: '0.875rem',
  },
}));

// ── Component ────────────────────────────────────────────────────────────────

const CustomPagination = ({
  count,
  page,                        // 0-based
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  labelRowsPerPage = 'Per page:',
  labelDisplayedRows,
  showRowsPerPage = true,
  showPageInfo = true,
  showTotalCount = true,
}) => {
  const theme = useTheme();
  const totalPages = Math.ceil(count / rowsPerPage);
  const isFirstPage = page === 0;
  const isLastPage = page >= totalPages - 1;

  const handlePrevious = () => { if (!isFirstPage) onPageChange(null, page - 1); };
  const handleNext    = () => { if (!isLastPage)  onPageChange(null, page + 1); };

  const getDisplayedRowsText = () => {
    if (labelDisplayedRows) {
      const from = page * rowsPerPage + 1;
      const to   = Math.min((page + 1) * rowsPerPage, count);
      return labelDisplayedRows({ from, to, count });
    }
    const from = page * rowsPerPage + 1;
    const to   = Math.min((page + 1) * rowsPerPage, count);
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
  };

  const generatePageNumbers = () => {
    const current = page + 1;
    const pages   = [];

    pages.push(1);

    const start = Math.max(2, current - 1);
    const end   = Math.min(totalPages - 1, current + 1);

    if (start > 2)             pages.push('...');
    for (let i = start; i <= end; i += 1) {
      if (i !== 1 && i !== totalPages) pages.push(i);
    }
    if (end < totalPages - 1)  pages.push('...');
    if (totalPages > 1)        pages.push(totalPages);

    return pages;
  };

  const handlePageClick = (pageNum) => {
    if (typeof pageNum === 'number') onPageChange(null, pageNum - 1);
  };

  const pageNumbers  = generatePageNumbers();
  const currentPage  = page + 1;

  return (
    <PaginationWrapper>
      {/* ── Left: range info ── */}
      {showPageInfo && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: '0 0 auto', minWidth: 'fit-content' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
            {getDisplayedRowsText()}
          </Typography>
          {showTotalCount && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)',
                color: '#fff',
                px: 1.5,
                py: 0.25,
                borderRadius: 2,
                display: 'inline-block',
                whiteSpace: 'nowrap',
              }}
            >
              Total: {count}
            </Typography>
          )}
        </Box>
      )}

      {/* ── Center: pill with page numbers ── */}
      <PaginationPill sx={{ flex: '0 0 auto' }}>
        <NavButton onClick={handlePrevious} disabled={isFirstPage} size="small">
          <ChevronLeft fontSize="small" />
        </NavButton>

        {pageNumbers.map((pageNum, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={idx}>
            {pageNum === '...' ? (
              <Typography sx={{ color: 'text.disabled', fontSize: '0.875rem', px: 0.5, userSelect: 'none' }}>
                …
              </Typography>
            ) : (
              <PageNumber active={pageNum === currentPage} onClick={() => handlePageClick(pageNum)}>
                {pageNum}
              </PageNumber>
            )}
          </React.Fragment>
        ))}

        <NavButton onClick={handleNext} disabled={isLastPage} size="small">
          <ChevronRight fontSize="small" />
        </NavButton>
      </PaginationPill>

      {/* ── Right: rows-per-page selector ── */}
      {showRowsPerPage && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto', minWidth: 'fit-content' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
            {labelRowsPerPage}
          </Typography>
          <RowsSelect size="small">
            <Select
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
              size="small"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === -1 ? 'All' : option}
                </MenuItem>
              ))}
            </Select>
          </RowsSelect>
        </Box>
      )}
    </PaginationWrapper>
  );
};

export default CustomPagination;
